import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// PatientHistory Modal Component
const FullPatientHistory = ({ patient, onClose }) => {
  const [expanded, setExpanded] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [pageConfig, setPageConfig] = useState({
    opd: 0,
    ipd: 0,
    radiology: 0,
    pharmacy: 0,
    billing: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    opd: { key: "", direction: "asc" },
    ipd: { key: "", direction: "asc" },
    radiology: { key: "", direction: "asc" },
    pharmacy: { key: "", direction: "asc" },
    billing: { key: "", direction: "asc" },
  });

  if (!patient) return null;

  // Sections with default empty arrays to avoid undefined
  const sections = [
    {
      name: "OPD",
      data: patient.opdHistory || [],
      columns: ["Visit No", "Visit Date", "Department", "Doctor", "Reason", "Status"],
      key: "opd",
    },
    {
      name: "IPD",
      data: patient.ipdHistory || [],
      columns: [
        "Admission No",
        "Admit Datetime",
        "Admission Reason",
        "Current Bed Id",
        "Doctor",
        "Status",
      ],
      key: "ipd",
    },
    {
      name: "Radiology",
      data: patient.radiologyHistory || [],
      columns: ["Order Datetime", "Source", "Priority", "Ordered By", "Status"],
      key: "radiology",
    },
    {
      name: "Pharmacy",
      data: patient.pharmacyHistory || [],
      columns: ["Bill No", "Bill Date", "Total Amount", "Paid Amount", "Payment Mode", "Status"],
      key: "pharmacy",
    },
    {
      name: "Billing",
      data: patient.billingHistory || [],
      columns: ["Invoice No", "Invoice Date", "Total Amount", "Net Amount", "Status"],
      key: "billing",
    },
  ];

  const handleExpand = (panel) => (e, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handlePageChange = (section) => (e, newPage) => {
    setPageConfig((prev) => ({ ...prev, [section]: newPage }));
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPageConfig({ opd: 0, ipd: 0, radiology: 0, pharmacy: 0, billing: 0 });
  };

  const handleSortClick = (section, col) => {
    const key = col.toLowerCase().replace(/ /g, "_");
    setSortConfig((prev) => {
      const direction =
        prev[section].key === key && prev[section].direction === "asc"
          ? "desc"
          : "asc";
      return { ...prev, [section]: { key, direction } };
    });
  };

  const getSortedData = (sectionData, section) => {
    const { key, direction } = sortConfig[section];
    if (!key) return sectionData;
    return [...sectionData].sort((a, b) => {
      if ((a[key] || "") < (b[key] || "")) return direction === "asc" ? -1 : 1;
      if ((a[key] || "") > (b[key] || "")) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const exportPDF = (sectionName, data, columns) => {
    const doc = new jsPDF();
    doc.text(`${patient.name} - ${sectionName}`, 14, 15);
    const tableData = (data || []).map((row) =>
      columns.map((col) => row[col.toLowerCase().replace(/ /g, "_")])
    );
    autoTable(doc, { head: [columns], body: tableData, startY: 20 });
    doc.save(`${patient.name}_${sectionName}.pdf`);
  };

  const exportPrint = (sectionName, data, columns) => {
    let html = `<h3>${patient.name} - ${sectionName}</h3><table border="1"><thead><tr>`;
    columns.forEach((col) => (html += `<th>${col}</th>`));
    html += "</tr></thead><tbody>";
    (data || []).forEach((row) => {
      html += "<tr>";
      columns.forEach((col) => {
        html += `<td>${row[col.toLowerCase().replace(/ /g, "_")] || ""}</td>`;
      });
      html += "</tr>";
    });
    html += "</tbody></table>";
    const w = window.open();
    w.document.write(html);
    w.document.close();
    w.print();
  };

  const fullHistoryCSV = sections.flatMap((sec) =>
    (sec.data || []).map((row) => ({ ...row, section: sec.name }))
  );

  const rowsPerPageOptions = [5, 10, 20];

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          bgcolor: "white",
          p: 4,
          borderRadius: 2,
          maxWidth: 1000,
          width: "95%",
          maxHeight: "90%",
          overflowY: "auto",
        }}
      >
        <Typography variant="h5" gutterBottom>
          📝 Full Patient History: {patient.name}
        </Typography>

        {/* Full History Export */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <CSVLink data={fullHistoryCSV} filename={`${patient.name}_FullHistory.csv`}>
            <Button variant="outlined">Export CSV</Button>
          </CSVLink>
          <Button
            variant="outlined"
            onClick={() => {
              const doc = new jsPDF();
              doc.text(`${patient.name} - Full History`, 14, 15);
              let y = 20;
              sections.forEach((sec) => {
                doc.text(sec.name, 14, y);
                y += 5;
                const tableData = (sec.data || []).map((row) =>
                  sec.columns.map((col) => row[col.toLowerCase().replace(/ /g, "_")] || "")
                );
                autoTable(doc, { head: [sec.columns], body: tableData, startY: y });
                y += tableData.length * 8 + 10;
              });
              doc.save(`${patient.name}_FullHistory.pdf`);
            }}
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              let html = `<h3>${patient.name} - Full History</h3>`;
              sections.forEach((sec) => {
                html += `<h4>${sec.name}</h4><table border="1"><thead><tr>`;
                sec.columns.forEach((col) => (html += `<th>${col}</th>`));
                html += "</tr></thead><tbody>";
                (sec.data || []).forEach((row) => {
                  html += "<tr>";
                  sec.columns.forEach((col) => {
                    html += `<td>${row[col.toLowerCase().replace(/ /g, "_")] || ""}</td>`;
                  });
                  html += "</tr>";
                });
                html += "</tbody></table><br/>";
              });
              const w = window.open();
              w.document.write(html);
              w.document.close();
              w.print();
            }}
          >
            Print Full History
          </Button>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </Box>

        {/* Accordion Sections */}
        {sections.map((sec) => {
          const sortedData = getSortedData(sec.data || [], sec.key);
          const paginatedData = sortedData.slice(
            pageConfig[sec.key] * rowsPerPage,
            pageConfig[sec.key] * rowsPerPage + rowsPerPage
          );

          return (
            <Accordion
              key={sec.key}
              expanded={expanded === sec.key}
              onChange={handleExpand(sec.key)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ flex: 1, fontWeight: "bold" }}>
                  {sec.name} ({(sec.data || []).length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", gap: 2, mb: 1, flexWrap: "wrap" }}>
                  <CSVLink data={sec.data || []} filename={`${patient.name}_${sec.name}.csv`}>
                    <Button variant="outlined" size="small">
                      CSV
                    </Button>
                  </CSVLink>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => exportPDF(sec.name, sec.data || [], sec.columns)}
                  >
                    PDF
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => exportPrint(sec.name, sec.data || [], sec.columns)}
                  >
                    Print
                  </Button>
                </Box>

                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {sec.columns.map((col) => {
                          const key = col.toLowerCase().replace(/ /g, "_");
                          return (
                            <TableCell
                              key={key}
                              sx={{ cursor: "pointer" }}
                              onClick={() => handleSortClick(sec.key, col)}
                            >
                              {col}{" "}
                              {sortConfig[sec.key].key === key
                                ? sortConfig[sec.key].direction === "asc"
                                  ? "▲"
                                  : "▼"
                                : ""}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.map((row, idx) => (
                        <TableRow key={idx}>
                          {sec.columns.map((col) => {
                            const key = col.toLowerCase().replace(/ /g, "_");
                            return <TableCell key={key}>{row[key] || ""}</TableCell>;
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                <TablePagination
                  component="div"
                  count={(sec.data || []).length}
                  page={pageConfig[sec.key]}
                  onPageChange={handlePageChange(sec.key)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={rowsPerPageOptions}
                />
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
};

export default FullPatientHistory;
